import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar,
  Button,
  useTheme,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import {
  Add,
  Refresh,
  CheckCircle,
  Cancel,
  Visibility,
  ContentCopy,
  PersonAdd,
  Schedule,
  Search,
  FilterList,
  OpenInNew,
  Clear,
  ExpandMore,
  ExpandLess,
  AttachFile,
  PendingActions,
  HourglassEmpty,
  Error as ErrorIcon,
  Save,
  AttachMoney,
  Wifi,
  Person,
  VisibilityOff,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Helmet } from 'react-helmet-async';
import preCadastroService, { PreCadastroAtendente } from '../services/preCadastroService';
import PreCadastroForm from '../components/forms/PreCadastroForm';
import PlanosSelect from '../components/selects/PlanosSelect';
import FuncionariosSelect from '../components/selects/FuncionariosSelect';
import EscritorioSelect from '../components/selects/EscritorioSelect';
import ClientSelect from '../components/selects/ClientSelect';
import NasSelect from '../components/selects/NasSelect';
import { planosService } from '../services/planosService';
import { useRelatorioCores } from './relatorios';
import {
  themedStatCardSx,
  themedStatIconBoxSx,
  filterBarHeaderSx,
  tableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

// ─── Status chip ──────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<
  string,
  { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }
> = {
  aguardando_assinatura: { label: 'Aguardando Assinatura', color: 'info' },
  aguardando_aprovacao: { label: 'Aguardando Aprovação', color: 'warning' },
  pendente: { label: 'Pendente', color: 'warning' },
  aprovado: { label: 'Aprovado', color: 'success' },
  reprovado: { label: 'Reprovado', color: 'error' },
  expirado: { label: 'Expirado', color: 'default' },
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'default' as const };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
    />
  );
};

// ─── Constants para formulário ──────────────────────────────────────────────
const TIPO_CONEXAO_OPTIONS = [
  { value: 'fibra', label: 'Fibra Óptica' },
  { value: 'radio', label: 'Rádio' },
  { value: 'cabo_coaxial', label: 'Cabo Coaxial' },
  { value: 'par_metalico', label: 'Par Metálico' },
  { value: 'satelite', label: 'Satélite' },
];

const TIPO_CLIENTE_OPTIONS = [
  { value: '01', label: '01 - Comercial' },
  { value: '02', label: '02 - Industrial' },
  { value: '03', label: '03 - Residencial/Pessoa Física' },
  { value: '04', label: '04 - Produtor Rural' },
  { value: '05', label: '05 - Órgão da administração pública estadual ICMS 107/95' },
  { value: '06', label: '06 - Prestador de serviço de telecomunicação ICMS 17/13' },
  { value: '07', label: '07 - Missões Diplomáticas ICMS 158/94' },
  { value: '08', label: '08 - Igrejas e Templos de qualquer natureza' },
  { value: '99', label: '99 - Outros' },
];

const COMO_CONHECEU_OPTIONS = [
  'Carro da empresa',
  'Facebook',
  'Google',
  'Indicação de amigo',
  'Instagram',
  'Outdoor',
  'Outro',
  'Panfleto',
  'Passou em frente à loja',
  'Rádio',
  'Site',
  'TikTok',
  'Whatsapp',
];

const CONTRIBUINTE_ICMS_OPTIONS = [
  { value: 1, label: '1 - Contribuinte ICMS' },
  { value: 2, label: '2 - Contribuinte isento de Inscrição no cadastro de Contribuintes do ICMS' },
  { value: 9, label: '9 - Não contribuinte ICMS' },
];

const formatCurrencyInput = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  if (!numericValue) return '';
  const cents = parseInt(numericValue, 10);
  const reais = cents / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(reais);
};

const parseCurrencyInput = (formattedValue: string): number => {
  const numericString = formattedValue.replace(/[R$\s.]/g, '').replace(',', '.');
  return parseFloat(numericString) || 0;
};

const formatCurrencyDisplay = (val?: number | null): string => {
  if (val === undefined || val === null) return '';
  if (val === 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
};

// ─── Página Principal ─────────────────────────────────────────────────────
const PreCadastrosPage: React.FC = () => {
  const theme = useTheme();
  const cores = useRelatorioCores();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<PreCadastroAtendente[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('todos');
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // Modais
  const [formOpen, setFormOpen] = useState(false);
  const [selectedPreId, setSelectedPreId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await preCadastroService.listar({
        status: filterStatus !== 'todos' ? filterStatus : undefined,
        page,
        limit: 25,
      });
      setItems(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) {
      const msg = err.message || 'Erro ao carregar pré-cadastros';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Contadores por status para os cards resumo
  const stats = useMemo(() => {
    const map = {
      aguardando_assinatura: 0,
      aguardando_aprovacao: 0,
      pendente: 0,
      aprovado: 0,
      reprovado: 0,
      total: total,
    };
    items.forEach((i) => {
      if (i.status === 'aguardando_assinatura') map.aguardando_assinatura++;
      else if (i.status === 'aguardando_aprovacao') map.aguardando_aprovacao++;
      else if (i.status === 'pendente') map.pendente++;
      else if (i.status === 'aprovado') map.aprovado++;
      else if (i.status === 'reprovado') map.reprovado++;
    });
    return map;
  }, [items, total]);

  // Filtragem local pelo termo de busca (cliente, CPF/CNPJ, plano, login)
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.toLowerCase();
    return items.filter((item) => {
      const nomeMatch = item.nome?.toLowerCase().includes(term);
      const docMatch = item.cpf_cnpj?.toLowerCase().includes(term);
      const idMatch = String(item.id).includes(term);
      const planoMatch = item.plano?.nome?.toLowerCase().includes(term);
      const pppoeMatch = item.pppoe_usuario?.toLowerCase().includes(term);
      return Boolean(nomeMatch || docMatch || idMatch || planoMatch || pppoeMatch);
    });
  }, [items, searchTerm]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('todos');
    setPage(1);
  };

  const hasActiveFilters = Boolean(searchTerm || filterStatus !== 'todos');

  const handleApprove = async (id: number) => {
    const item = items.find((i) => i.id === id);
    const isAguardandoAprovacao = item?.status === 'aguardando_aprovacao';

    const confirm = await Swal.fire({
      title: isAguardandoAprovacao ? 'Aprovar pré-cadastro?' : 'Gerar Cadastro/Contrato?',
      html: isAguardandoAprovacao
        ? 'Ao aprovar, o status mudará para <b>Aguardando Assinatura</b> e o link ficará disponível.'
        : 'Isso irá criar automaticamente:<br><b>✅ Cliente</b><br><b>✅ Contrato</b><br><b>✅ Serviço</b><br><b>✅ Chamado de instalação</b>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: isAguardandoAprovacao ? 'Sim, aprovar!' : 'Sim, gerar!',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#22c55e',
    });
    if (!confirm.isConfirmed) return;

    try {
      const result: any = await preCadastroService.aprovar(id);
      setSelectedPreId(null);
      fetchData();

      if (result?.cliente_id && result?.contrato_id) {
        toast.success('✅ Aprovado! Cliente e contrato criados com sucesso.');
        Swal.fire({
          title: 'Aprovado!',
          html: `<p>Cliente <b>#${result.cliente_id}</b> e Contrato <b>#${result.contrato_id}</b> criados.</p>`,
          icon: 'success',
          confirmButtonText: 'Ver cliente',
        }).then((r) => {
          if (r.isConfirmed) navigate(`/clients/${result.cliente_id}`);
        });
      } else {
        toast.success('✅ Pré-cadastro aprovado! Status alterado para Aguardando Assinatura.');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao aprovar');
    }
  };

  const handleReject = async (id: number) => {
    const { value: motivo } = await Swal.fire({
      title: 'Reprovar pré-cadastro',
      input: 'textarea',
      inputLabel: 'Motivo da reprovação',
      inputPlaceholder: 'Informe o motivo...',
      showCancelButton: true,
      confirmButtonText: 'Reprovar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
    });
    if (motivo === undefined) return;

    try {
      await preCadastroService.reprovar(id, motivo);
      toast.success('Pré-cadastro reprovado.');
      setSelectedPreId(null);
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao reprovar');
    }
  };

  const copyLink = (link?: string) => {
    if (link) {
      navigator.clipboard.writeText(link);
      toast.success('Link copiado com sucesso!');
    }
  };

  return (
    <Box sx={{ p: 0.5 }}>
      <Helmet>
        <title>Pré-Cadastros | SysUp</title>
      </Helmet>

      {/* Header Padronizado idêntico ao ChamadosPage */}
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(cores.primario, 0.04)} 0%, ${alpha(
            cores.secundario,
            0.04
          )} 100%)`,
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: alpha(cores.primario, 0.12),
                color: cores.primario,
                width: 40,
                height: 40,
              }}
            >
              <PersonAdd sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Pré-Cadastros de Clientes
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Formulários públicos e aprovação de novos cadastros
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip title="Atualizar dados">
              <IconButton
                size="small"
                onClick={fetchData}
                disabled={loading}
                sx={{ border: `1px solid ${theme.palette.divider}` }}
              >
                {loading ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              startIcon={<Add sx={{ fontSize: 16 }} />}
              onClick={() => setFormOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[2] },
              }}
            >
              Novo Pré-Cadastro
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Cards de Resumo idênticos aos de ChamadosPage */}
      <Grid container spacing={1.5} mb={1}>
        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              ...themedStatCardSx(theme, 'warning'),
              cursor: 'pointer',
              border:
                filterStatus === 'pendente'
                  ? `2px solid ${theme.palette.warning.main}`
                  : `1px solid ${theme.palette.divider}`,
            }}
            onClick={() => setFilterStatus(filterStatus === 'pendente' ? 'todos' : 'pendente')}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Aguardando Cliente
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="warning.main" sx={{ mt: 0.5 }}>
                    {loading ? '...' : stats.pendente}
                  </Typography>
                </Box>
                <Box sx={themedStatIconBoxSx(theme, 'warning')}>
                  <HourglassEmpty sx={{ fontSize: 28, color: 'warning.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              ...themedStatCardSx(theme, 'info'),
              cursor: 'pointer',
              border:
                filterStatus === 'aguardando_aprovacao'
                  ? `2px solid ${theme.palette.info.main}`
                  : `1px solid ${theme.palette.divider}`,
            }}
            onClick={() =>
              setFilterStatus(filterStatus === 'aguardando_aprovacao' ? 'todos' : 'aguardando_aprovacao')
            }
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Para Aprovação
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="info.main" sx={{ mt: 0.5 }}>
                    {loading ? '...' : stats.aguardando_aprovacao}
                  </Typography>
                </Box>
                <Box sx={themedStatIconBoxSx(theme, 'info')}>
                  <PendingActions sx={{ fontSize: 28, color: 'info.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              ...themedStatCardSx(theme, 'success'),
              cursor: 'pointer',
              border:
                filterStatus === 'aprovado'
                  ? `2px solid ${theme.palette.success.main}`
                  : `1px solid ${theme.palette.divider}`,
            }}
            onClick={() => setFilterStatus(filterStatus === 'aprovado' ? 'todos' : 'aprovado')}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Aprovados
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main" sx={{ mt: 0.5 }}>
                    {loading ? '...' : stats.aprovado}
                  </Typography>
                </Box>
                <Box sx={themedStatIconBoxSx(theme, 'success')}>
                  <CheckCircle sx={{ fontSize: 28, color: 'success.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              ...themedStatCardSx(theme, 'error'),
              cursor: 'pointer',
              border:
                filterStatus === 'reprovado'
                  ? `2px solid ${theme.palette.error.main}`
                  : `1px solid ${theme.palette.divider}`,
            }}
            onClick={() => setFilterStatus(filterStatus === 'reprovado' ? 'todos' : 'reprovado')}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Reprovados
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="error.main" sx={{ mt: 0.5 }}>
                    {loading ? '...' : stats.reprovado}
                  </Typography>
                </Box>
                <Box sx={themedStatIconBoxSx(theme, 'error')}>
                  <ErrorIcon sx={{ fontSize: 28, color: 'error.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Caixa de Filtros com Collapse idêntica à de ChamadosPage */}
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box sx={filterBarHeaderSx(theme)} onClick={() => setFiltersExpanded(!filtersExpanded)}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${filteredItems.length} resultado(s)`}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <IconButton size="small">
            {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={filtersExpanded}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por cliente, CPF/CNPJ, plano, login PPPoE ou ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      setPage(1);
                    }}
                    sx={{ borderRadius: 2 }}
                    size="small"
                  >
                    <MenuItem value="todos">Todos os Status</MenuItem>
                    <MenuItem value="aguardando_aprovacao">Aguardando Aprovação (Site)</MenuItem>
                    <MenuItem value="aguardando_assinatura">Aguardando Assinatura</MenuItem>
                    <MenuItem value="pendente">Pendente (Assinado pelo cliente)</MenuItem>
                    <MenuItem value="aprovado">Aprovado</MenuItem>
                    <MenuItem value="reprovado">Reprovado</MenuItem>
                    <MenuItem value="expirado">Expirado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={2} md={2}>
                {hasActiveFilters && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={clearFilters}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Limpar
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Título da Seção */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} mt={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>
          Lista de Pré-Cadastros
          {hasActiveFilters && (
            <Chip
              label={`${filteredItems.length} de ${total}`}
              size="small"
              sx={{ ml: 1.5, height: 24 }}
            />
          )}
        </Typography>
      </Box>

      {/* Conteúdo da Tabela / Estado Vazio */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Carregando pré-cadastros...
          </Typography>
        </Box>
      ) : filteredItems.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <PersonAdd sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {hasActiveFilters ? 'Nenhum pré-cadastro encontrado' : 'Nenhum pré-cadastro cadastrado'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {hasActiveFilters
              ? 'Tente ajustar os filtros para encontrar mais resultados'
              : 'Clique em "Novo Pré-Cadastro" para criar o primeiro link de formulário'}
          </Typography>
          {hasActiveFilters ? (
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={clearFilters}
              sx={{ mt: 3, borderRadius: 2, textTransform: 'none' }}
            >
              Limpar Filtros
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setFormOpen(true)}
              sx={{ mt: 3, borderRadius: 2, textTransform: 'none' }}
            >
              Novo Pré-Cadastro
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={2}
          sx={{
            borderRadius: 2,
            overflowX: 'auto',
            mb: 2,
            border: 'none',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 800,
              '& .MuiTableBody-root .MuiTableCell-root': {
                py: 0.6,
                fontSize: '0.78rem',
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  ...tableHeadRowSx(theme),
                  '& .MuiTableCell-head': {
                    bgcolor: (t) =>
                      `color-mix(in srgb, ${theme.palette.primary.main} 12%, ${t.palette.background.paper})`,
                    fontWeight: 700,
                    fontSize: '0.73rem',
                    py: 0.8,
                    whiteSpace: 'nowrap',
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: 'text.primary',
                  },
                }}
              >
                <TableCell sx={{ width: 80 }}>ID</TableCell>
                <TableCell>Cliente / Documento</TableCell>
                <TableCell>Plano / Valor</TableCell>
                <TableCell sx={{ width: 140 }}>Status</TableCell>
                <TableCell>Instalação</TableCell>
                <TableCell>Vendedor</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell>Assinado em</TableCell>
                <TableCell align="right" sx={{ width: 110 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => setSelectedPreId(item.id)}
                >
                  <TableCell sx={{ py: 0.6, px: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}
                    >
                      #{item.id.toString().padStart(5, '0')}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: '0.82rem', lineHeight: 1.2 }}
                    >
                      {item.nome || (
                        <Typography
                          component="span"
                          variant="caption"
                          color="warning.main"
                          fontWeight={600}
                        >
                          Aguardando preenchimento
                        </Typography>
                      )}
                    </Typography>
                    {item.cpf_cnpj ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.68rem', lineHeight: 1 }}
                      >
                        {item.cpf_cnpj}
                      </Typography>
                    ) : item.telefone ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.68rem', lineHeight: 1 }}
                      >
                        {item.telefone}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                      {item.plano?.nome || '-'}
                    </Typography>
                    {item.valor_contratado && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.68rem', lineHeight: 1 }}
                      >
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(item.valor_contratado)}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ py: 0.6, px: 2 }}>
                    <StatusChip status={item.status} />
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                    {item.data_instalacao_agendada ? (
                      <Tooltip
                        title={new Date(item.data_instalacao_agendada).toLocaleString('pt-BR')}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                            {new Date(item.data_instalacao_agendada).toLocaleDateString('pt-BR')}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            {new Date(item.data_instalacao_agendada).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      {item.vendedor?.nome ? item.vendedor.nome.split(' ')[0] : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                    <Tooltip title={new Date(item.createdAt).toLocaleString('pt-BR')}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                          {format(new Date(item.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          {format(new Date(item.createdAt), 'HH:mm', { locale: ptBR })}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.6, px: 2 }}>
                    {item.assinado_em ? (
                      <Tooltip title={new Date(item.assinado_em).toLocaleString('pt-BR')}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                            {format(new Date(item.assinado_em), 'dd/MM/yyyy', { locale: ptBR })}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            {format(new Date(item.assinado_em), 'HH:mm', { locale: ptBR })}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}
                      >
                        (Aguardando)
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ py: 0.6, px: 2 }}>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.link_cliente && item.status === 'pendente' && (
                        <Tooltip title="Copiar link do cliente">
                          <IconButton
                            size="small"
                            sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}
                            onClick={() => copyLink(item.link_cliente)}
                          >
                            <ContentCopy sx={{ fontSize: 18 }} color="info" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Ver detalhes">
                        <IconButton
                          size="small"
                          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                          onClick={() => setSelectedPreId(item.id)}
                        >
                          <Visibility sx={{ fontSize: 18 }} color="primary" />
                        </IconButton>
                      </Tooltip>

                      {item.status === 'aguardando_aprovacao' && (
                        <>
                          <Tooltip title="Reprovar">
                            <IconButton
                              size="small"
                              sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}
                              onClick={() => handleReject(item.id)}
                            >
                              <Cancel sx={{ fontSize: 18 }} color="error" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revisar e Aprovar">
                            <IconButton
                              size="small"
                              sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}
                              onClick={() => setSelectedPreId(item.id)}
                            >
                              <CheckCircle sx={{ fontSize: 18 }} color="success" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Barra inferior e paginação no mesmo padrão */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.8,
              px: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.75rem', fontWeight: 500 }}
            >
              Mostrando <strong>{filteredItems.length}</strong> de <strong>{total}</strong> pré-cadastro
              {total !== 1 ? 's' : ''}
            </Typography>

            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                size="small"
                color="primary"
                shape="rounded"
              />
            )}
          </Box>
        </TableContainer>
      )}

      {/* Modal de Criação ou Edição/Revisão de Pré-Cadastro */}
      <PreCadastroForm
        open={formOpen || Boolean(selectedPreId)}
        preCadastroId={selectedPreId}
        onClose={() => {
          setFormOpen(false);
          setSelectedPreId(null);
        }}
        onSuccess={() => {
          setFormOpen(false);
          setSelectedPreId(null);
          fetchData();
        }}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </Box>
  );
};

export default PreCadastrosPage;
